// JanetDraftReview.jsx
// Presentational review pane for a Janet-generated email draft: review flags,
// editable subject, optional CC/BCC, body editor (rich preview ↔ HTML source),
// tone check, and a "Steer Janet" regenerate box. Fully controlled via props so
// it can be reused by both JanetDraftModal (single reviewed send) and
// SubmissionComposer (batch review queue). It renders NO footer/send button —
// each host supplies its own action row and send logic.

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCw, Code, ChevronDown, Loader2 } from 'lucide-react'
import EmailRecipientField from '../ui/EmailRecipientField'

export default function JanetDraftReview({
  draft,                       // { review_flags[], tone_check } — for the flag banner + tone line
  isCritical = false,          // CP-1 → red banner line
  editedSubject, setEditedSubject,
  editedBody, setEditedBody,
  to, setTo,                   // optional — pass setTo to show an editable To row (reply-all drafts)
  cc, setCc, bcc, setBcc,      // optional — omit setCc/setBcc to hide the CC/BCC row
  showCcBcc = true,
  extraInstructions, setExtraInstructions,
  onRegenerate,                // () => void
  generating = false,
  headerSlot = null,           // optional node rendered above the flags (e.g. attachments summary)
}) {
  const [showSource, setShowSource] = useState(false)
  const [showGuidance, setShowGuidance] = useState(false)
  const canCcBcc = showCcBcc && typeof setCc === 'function' && typeof setBcc === 'function'

  return (
    <div className="space-y-4">
      {headerSlot}

      {/* Review flags */}
      {(draft?.review_flags?.length > 0 || isCritical) && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 space-y-0.5">
            {isCritical && <p className="font-semibold">CP-1 contact — verify every detail before sending</p>}
            {draft?.review_flags?.map((f, i) => <p key={i}>{f}</p>)}
          </div>
        </div>
      )}

      {/* Subject */}
      <div>
        <label className="block text-xs font-medium text-warm-600 mb-1">Subject</label>
        <input
          type="text"
          value={editedSubject}
          onChange={e => setEditedSubject(e.target.value)}
          className="input-field w-full text-sm"
        />
      </div>

      {/* To — only shown when the host controls it (reply-all drafts) */}
      {typeof setTo === 'function' && (
        <EmailRecipientField label="To" value={to} onChange={setTo} />
      )}

      {/* CC / BCC */}
      {canCcBcc && (
        <div className="grid grid-cols-2 gap-2">
          <EmailRecipientField label="CC" value={cc} onChange={setCc} />
          <EmailRecipientField label="BCC" value={bcc} onChange={setBcc} />
        </div>
      )}

      {/* Body */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-warm-600">Message</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSource(s => !s)}
              className="flex items-center gap-1 text-[10px] text-warm-400 hover:text-navy-600 transition-colors"
              title={showSource ? 'Show preview' : 'Edit HTML source'}
            >
              <Code size={10} />
              {showSource ? 'Preview' : 'Edit source'}
            </button>
            {onRegenerate && (
              <button
                onClick={onRegenerate}
                disabled={generating}
                className="flex items-center gap-1 text-[10px] text-warm-400 hover:text-navy-600 transition-colors"
              >
                <RefreshCw size={10} className={generating ? 'animate-spin' : ''} />
                Regenerate
              </button>
            )}
          </div>
        </div>
        {showSource ? (
          <textarea
            value={editedBody}
            onChange={e => setEditedBody(e.target.value)}
            rows={12}
            className="input-field w-full text-sm resize-none font-mono text-xs leading-relaxed"
          />
        ) : (
          <div
            className="border border-warm-200 rounded-lg bg-white p-4 overflow-auto text-sm break-words"
            style={{ maxHeight: 340 }}
            dangerouslySetInnerHTML={{ __html: editedBody || '<p class="text-warm-400 text-xs">(empty)</p>' }}
          />
        )}
      </div>

      {/* Tone check */}
      {draft?.tone_check && (
        <p className="text-[10px] text-warm-400 flex items-center gap-1">
          <CheckCircle2 size={10} className="text-green-500" />
          {draft.tone_check}
        </p>
      )}

      {/* Steer Janet — guidance + regenerate */}
      {onRegenerate && typeof setExtraInstructions === 'function' && (
        <div className="border border-warm-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowGuidance(s => !s)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-warm-500 hover:text-warm-700 hover:bg-warm-50 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <RefreshCw size={10} />
              Steer Janet
            </span>
            <ChevronDown size={10} className={`transition-transform ${showGuidance ? 'rotate-180' : ''}`} />
          </button>
          {showGuidance && (
            <div className="px-3 pb-3 space-y-2 border-t border-warm-100 pt-2">
              <textarea
                value={extraInstructions}
                onChange={e => setExtraInstructions(e.target.value)}
                autoFocus
                rows={2}
                placeholder={`e.g. "Mention we spoke at the festival" or "Keep it shorter"`}
                className="input-field w-full text-sm resize-none"
              />
              <button
                onClick={onRegenerate}
                disabled={generating}
                className="btn-secondary gap-1.5 text-xs w-full justify-center"
              >
                {generating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                Regenerate with guidance
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
